import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './components/auth/AuthContext';
import { CompanyProvider } from './components/Company/CompanyContext';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './styles/mobile-fixes.css';
import './styles/error-toast.css';
import { motion, AnimatePresence } from 'framer-motion';

// Components
import Login from './components/auth/Login';
import Signup from './components/auth/Signup';
import AuthLayout from './components/auth/AuthLayout';
import CompanyConfig from './components/Company/CompanyConfig';
import NavbarSidebar from './components/layout/NavbarSidebar';
import TopBar from './components/layout/TopBar';
import ChatContainer from './components/chat/ChatContainer';
import WelcomeChat from './components/chat/WelcomeChat';
import Dashboard from './components/dashboard/Dashboard';
import UITest from './components/test/UITest';
import CompanyConfigTest from './components/test/CompanyConfigTest';
import CelebrationTest from './components/test/CelebrationTest';
import GoogleCalendarCallback from './components/auth/GoogleCalendarCallback';
import GoogleGmailCallback from './components/auth/GoogleGmailCallback';
import CalendarTest from './components/test/CalendarTest';
import EnvTest from './components/test/EnvTest';
import StorageTest from './components/test/StorageTest';
import ManualCallback from './components/test/ManualCallback';
import GlobalErrorBoundary from './components/common/GlobalErrorBoundary';
import PrivacyPolicy from './components/legal/PrivacyPolicy';
import AnimatedBackground, { ThemeProvider } from './components/layout/AnimatedBackground';

// Require authentication wrapper
const RequireAuth = ({ children }) => {
  const { currentUser, userProfile, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-orange-500"></div>
      </div>
    );
  }
  
  if (!currentUser) {
    return <Navigate to="/login" />;
  }
  
  // If user is not configured, redirect to company config
  if (userProfile && !userProfile.isConfigured) {
    return <Navigate to="/company-config" />;
  }
  
  return children;
};

// Company config protection
const RequireNoConfig = ({ children }) => {
  const { currentUser, userProfile, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-orange-500"></div>
      </div>
    );
  }
  
  if (!currentUser) {
    return <Navigate to="/login" />;
  }
  
  // If user is already configured, redirect to home
  if (userProfile && userProfile.isConfigured) {
    return <Navigate to="/" />;
  }
  
  return children;
};

// Auth protection
const RequireNoAuth = ({ children }) => {
  const { currentUser, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-orange-500"></div>
      </div>
    );
  }
  
  if (currentUser) {
    return <Navigate to="/" />;
  }
  
  return children;
};

// Custom hook for detecting mobile devices
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    // Check initial
    checkMobile();
    
    // Add event listener
    window.addEventListener('resize', checkMobile);
    
    // Clean up
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  return isMobile;
};

// New integrated chat layout
const ChatLayout = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const isMobile = useIsMobile();
  
  // Handle mobile sidebar toggle
  const toggleMobileSidebar = () => {
    setIsMobileSidebarOpen(!isMobileSidebarOpen);
  };
  
  // Close mobile sidebar
  const closeMobileSidebar = () => {
    setIsMobileSidebarOpen(false);
  };
  
  return (
    <div className="flex flex-col h-[100dvh] max-h-[-webkit-fill-available] mobile-viewport-fix no-bounce relative">
      {/* TopBar - hide on mobile when sidebar is open */}
      <motion.div 
        className="w-full relative z-20 overflow-hidden"
        animate={{ 
          height: isExpanded || (isMobile && isMobileSidebarOpen) ? 0 : 'auto',
          opacity: isExpanded || (isMobile && isMobileSidebarOpen) ? 0 : 1,
          marginTop: isExpanded || (isMobile && isMobileSidebarOpen) ? -60 : 0
        }}
        transition={{ 
          duration: 0.4, 
          ease: [0.4, 0, 0.2, 1] 
        }}
      >
        <TopBar onMenuClick={isMobile ? toggleMobileSidebar : undefined} />
      </motion.div>
      <div className="flex flex-1 overflow-hidden relative z-10">
        {/* Mobile Sidebar Overlay */}
        <AnimatePresence>
          {isMobile && isMobileSidebarOpen && (
            <motion.div 
              className="fixed inset-0 bg-black/30 z-50"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeMobileSidebar}
            />
          )}
        </AnimatePresence>
        
        {/* Mobile Sidebar Drawer */}
        <AnimatePresence>
          {isMobile && (
            <motion.div 
              className="fixed top-0 left-0 h-full w-72 md:w-80 z-50 bg-white/90 backdrop-blur-xl shadow-lg"
              initial={{ x: "-100%" }}
              animate={{ x: isMobileSidebarOpen ? 0 : "-100%" }}
              exit={{ x: "-100%" }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
            >
              <div className="h-full">
                <NavbarSidebar isMobile={true} onCloseMobile={closeMobileSidebar} />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        
        {/* Desktop NavbarSidebar */}
        {!isMobile && (
          <motion.div 
            className="flex-shrink-0 h-full hidden md:block"
            animate={{ 
              width: isExpanded ? 0 : 288, // 288px = 72rem
              opacity: isExpanded ? 0 : 1,
              marginLeft: isExpanded ? -288 : 0
            }}
            transition={{ 
              duration: 0.4, 
              ease: [0.4, 0, 0.2, 1] 
            }}
          >
            <NavbarSidebar isExpanded={isExpanded} />
          </motion.div>
        )}
        
        {/* Chat Container */}
        <motion.div 
          className="flex-1 h-full overflow-hidden"
          animate={{ 
            padding: isExpanded ? 0 : isMobile ? 8 : 16
          }}
          transition={{ 
            duration: 0.4,
            ease: [0.4, 0, 0.2, 1]
          }}
        >
          <ChatContainer 
            onToggleExpand={() => setIsExpanded(!isExpanded)} 
            isExpanded={isExpanded} 
          />
        </motion.div>
      </div>
    </div>
  );
};

// Main layout for other authenticated pages
const MainLayout = ({ children }) => {
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const isMobile = useIsMobile();
  
  // Toggle mobile sidebar
  const toggleMobileSidebar = () => {
    setIsMobileSidebarOpen(!isMobileSidebarOpen);
  };
  
  // Close mobile sidebar
  const closeMobileSidebar = () => {
    setIsMobileSidebarOpen(false);
  };
  
  return (
    <div className="flex flex-col h-[100dvh] max-h-[-webkit-fill-available] mobile-viewport-fix no-bounce relative">
      {/* TopBar - hide on mobile when sidebar is open */}
      <motion.div 
        className="w-full relative z-20"
        animate={{
          height: isMobile && isMobileSidebarOpen ? 0 : 'auto',
          opacity: isMobile && isMobileSidebarOpen ? 0 : 1,
          marginTop: isMobile && isMobileSidebarOpen ? -60 : 0
        }}
        transition={{ duration: 0.3 }}
      >
        <TopBar onMenuClick={isMobile ? toggleMobileSidebar : undefined} />
      </motion.div>
      
      {/* Mobile Sidebar Overlay */}
      <AnimatePresence>
        {isMobile && isMobileSidebarOpen && (
          <motion.div 
            className="fixed inset-0 bg-black/30 z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeMobileSidebar}
          />
        )}
      </AnimatePresence>
      
      {/* Mobile Sidebar Drawer */}
      <AnimatePresence>
        {isMobile && (
          <motion.div 
            className="fixed top-0 left-0 h-full w-72 z-50 bg-white/90 backdrop-blur-xl shadow-lg"
            initial={{ x: "-100%" }}
            animate={{ x: isMobileSidebarOpen ? 0 : "-100%" }}
            exit={{ x: "-100%" }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
          >
            <div className="h-full">
              <NavbarSidebar isMobile={true} onCloseMobile={closeMobileSidebar} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {!isMobile && (
        <div className="w-full relative z-10">
          <NavbarSidebar isExpanded={false} />
        </div>
      )}
      
      <div className="flex-1 overflow-auto relative z-10 p-4">
        <div className="bg-white rounded-3xl shadow-sm overflow-hidden h-full">
          {children}
        </div>
      </div>
    </div>
  );
};

function App() {
  // Add viewport height calculation for mobile browsers
  useEffect(() => {
    // First we get the viewport height and multiply it by 1% to get a value for a vh unit
    const calculateVh = () => {
      let vh = window.innerHeight * 0.01;
      // Then we set the value in the --vh custom property to the root of the document
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };
    
    // Initial calculation
    calculateVh();
    
    // Recalculate on resize and orientation change
    window.addEventListener('resize', calculateVh);
    window.addEventListener('orientationchange', calculateVh);
    
    return () => {
      window.removeEventListener('resize', calculateVh);
      window.removeEventListener('orientationchange', calculateVh);
    };
  }, []);

  return (
    <GlobalErrorBoundary>
      <Router>
        <ThemeProvider>
          {/* AnimatedBackground positioned as the app background */}
          <div className="fixed inset-0 z-0">
            <AnimatedBackground />
          </div>
          
          <AuthProvider>
            <CompanyProvider>
              <Routes>
                {/* Auth routes with shared layout */}
                <Route element={<AuthLayout />}>
                  <Route path="/login" element={<RequireNoAuth><Login /></RequireNoAuth>} />
                  <Route path="/signup" element={<RequireNoAuth><Signup /></RequireNoAuth>} />
                  <Route path="/company-config" element={<RequireNoConfig><CompanyConfig /></RequireNoConfig>} />
                </Route>
                
                {/* Dashboard route */}
                <Route path="/dashboard" element={
                  <RequireAuth>
                    <MainLayout>
                      <Dashboard />
                    </MainLayout>
                  </RequireAuth>
                } />
                
                {/* UI Test routes */}
                <Route path="/ui-test" element={<UITest />} />
                <Route path="/config-test" element={<CompanyConfigTest />} />
                <Route path="/celebration-test" element={<CelebrationTest />} />
                
                {/* Chat routes - use new integrated layout */}
                <Route path="/" element={
                  <RequireAuth>
                    <ChatLayout />
                  </RequireAuth>
                } />
                <Route path="/chat/:chatId" element={
                  <RequireAuth>
                    <ChatLayout />
                  </RequireAuth>
                } />
                
                {/* Legal pages */}
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />

                {/* Add Google Calendar OAuth callback route */}
                <Route path="/auth/google-calendar-callback" element={<GoogleCalendarCallback />} />
                
                {/* Add Google Gmail OAuth callback route */}
                <Route path="/auth/google-gmail-callback" element={<GoogleGmailCallback />} />
                
                {/* Test routes */}
                <Route path="/test/calendar" element={
                  <RequireAuth>
                    <CalendarTest />
                  </RequireAuth>
                } />
                
                <Route path="/test/env" element={
                  <EnvTest />
                } />
                
                <Route path="/test/storage" element={
                  <StorageTest />
                } />
                
                <Route path="/test/manual-callback" element={
                  <ManualCallback />
                } />
                
                {/* Catch-all redirect */}
                <Route path="*" element={<Navigate to="/" />} />
              </Routes>
              
              {/* Toast container for notifications */}
              <ToastContainer
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
                limit={3}
              />
            </CompanyProvider>
          </AuthProvider>
        </ThemeProvider>
      </Router>
    </GlobalErrorBoundary>
  );
}

// Initialize the theme in local storage to ensure Peach theme is used
if (typeof window !== 'undefined') {
  localStorage.setItem('selectedThemeName', 'Peach');
}

export default App;